/**
 * Application Constants
 * Centralized configuration and constants
 */

import { env } from "@/config/env";

// ============================================================
// API Configuration
// ============================================================

export const API_URL = env.apiUrl;

export const WS_URL = env.wsUrl;

// ============================================================
// WebRTC Configuration
// ============================================================

export const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: env.stunUrl },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
    { urls: "stun:stun3.l.google.com:19302" },
  ],
  // Safari-friendly settings
  iceCandidatePoolSize: 10,
};

// ============================================================
// Media Constraints
// ============================================================

export const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: "user",
  width: { ideal: 1280 },
  height: { ideal: 720 },
};

export const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

// ============================================================
// Storage Keys
// ============================================================

export const STORAGE_KEYS = {
  deviceType: "goodbaby_device_type",
  deviceFingerprint: "goodbaby_device_fingerprint",
  lastMotionTimestamp: "goodbaby_last_motion_timestamp",
  motionDetectionEnabled: "goodbaby_motion_detection_enabled",
  motionSensitivityLevel: "goodbaby_motion_sensitivity_level",
  cameraFacingMode: "goodbaby_camera_facing_mode",
} as const;

// ============================================================
// Motion Detection Sensitivity Levels
// ============================================================

export type MotionSensitivityLevel = "whisper" | "aware" | "vigilant";

export const MOTION_SENSITIVITY_LEVELS: Record<
  MotionSensitivityLevel,
  { label: string; description: string; threshold: number }
> = {
  whisper: {
    label: "Low",
    description: "Only significant movements",
    threshold: 0.15, // Higher threshold = less sensitive
  },
  aware: {
    label: "Medium",
    description: "Balanced detection",
    threshold: 0.08, // Default/medium sensitivity
  },
  vigilant: {
    label: "High",
    description: "Catches subtle movements",
    threshold: 0.03, // Lower threshold = more sensitive
  },
} as const;

export const DEFAULT_SENSITIVITY_LEVEL: MotionSensitivityLevel = "aware";

// ============================================================
// Timing Constants
// ============================================================

export const TIMING = {
  motionAlertDuration: 3000, // 3 seconds
  audioAnalyserFFTSize: 256,
  audioSmoothingConstant: 0.8,
} as const;

// ============================================================
// Lullaby Configuration
// ============================================================

export * from "./lullabies";
